import React from "react";

const FONT_FAMILY = "AdigianaToybox";
const FONT_URL = "/assets/fonts/AdigianaToybox.ttf";
const FONT_STACK = `"${FONT_FAMILY}"`;
const FONT_FACE_ID = "etsy-template3-adigiana-toybox-font-face";

const OVERLAY_SRC = "/assets/templates/pinterest-etsy-template3-overlay.svg";

// Black outline built from offset copies behind the white fill — more reliable under
// html2canvas capture than -webkit-text-stroke or text-shadow, which it renders inconsistently.
const KEYWORD_OUTLINE_OFFSETS = [
  [-3, -3], [0, -3], [3, -3],
  [-3, 0], [3, 0],
  [-3, 3], [0, 3], [3, 3]
];

function useLocalFont() {
  if (typeof document !== "undefined") {
    if (!document.getElementById(FONT_FACE_ID)) {
      const style = document.createElement("style");
      style.id = FONT_FACE_ID;
      style.textContent = `
        @font-face {
          font-family: "${FONT_FAMILY}";
          src: url("${FONT_URL}") format("truetype");
          font-weight: normal;
          font-style: normal;
          font-display: block;
        }
      `;
      document.head.appendChild(style);
    }
    // Kick off the font fetch/parse immediately so it's ready well before html2canvas
    // captures the DOM (capture happens after images load, so this has ample time).
    if (document.fonts && document.fonts.load) {
      document.fonts.load(`65px "${FONT_FAMILY}"`).catch(() => {});
    }
  }
}

// Etsy titles are long, comma-separated SEO strings (e.g. "Personalized Dog Mom Bracelet,
// Custom Pet Name Jewelry, Gift for Dog Lovers"). Only the first, most relevant segment is
// used as the on-image keyword — never the full listing title.
function shortenEtsyKeyword(text) {
  if (!text) return "";
  const firstSegment = (text.split(/[,|–—]/)[0] || text).trim();
  const words = firstSegment.split(/\s+/).filter(Boolean);
  const MAX_WORDS = 6;
  return words.length > MAX_WORDS ? words.slice(0, MAX_WORDS).join(" ") : firstSegment;
}

export default function EtsyPinTemplate3({
  foodImageUrl1,
  foodImageUrl2,
  title = "Handmade Gift",
  focusKeyword
}) {
  useLocalFont();

  const listingImage = foodImageUrl1 || foodImageUrl2;
  const displayKeyword = shortenEtsyKeyword(focusKeyword || title);

  const keywordTextStyle = {
    fontFamily: FONT_STACK,
    fontWeight: "normal",
    fontSize: 65,
    lineHeight: 1.25,
    textAlign: "center",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word"
  };

  return (
    <div
      id="pinterest-pin-template"
      style={{
        width: 1000,
        height: 1250,
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#c8a882",
        WebkitFontSmoothing: "antialiased"
      }}
    >
      {/* ===== LAYER 1: ETSY LISTING IMAGE — square, pinned to the top; the lower
          area (below y:1000) is left clear for the template background/design ===== */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: 1000,
          height: 1000,
          overflow: "hidden",
          backgroundColor: "#c8a882"
        }}
      >
        {listingImage && (
          <img
            src={listingImage}
            alt=""
            crossOrigin="anonymous"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              display: "block"
            }}
          />
        )}
      </div>

      {/* ===== LAYER 2: FIXED SVG OVERLAY (unmodified design asset) ===== */}
      <img
        src={OVERLAY_SRC}
        alt=""
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: 1000,
          height: 1250,
          zIndex: 10,
          pointerEvents: "none"
        }}
      />

      {/* ===== LAYER 3: DYNAMIC FOCUS KEYWORD — upper part of the colored block,
          confined to the left area so it never overlaps the plate/hand graphic
          on the right (that graphic occupies roughly x:825-1000, y:1012-1249 of
          this 1000x1250 canvas). Centered within that left area, not the full
          canvas — hence the fixed pixel "left" instead of "left: 50%". ===== */}
      <div
        style={{
          position: "absolute",
          left: 30,
          top: 1100,
          transform: "translateY(-50%)",
          width: 750,
          zIndex: 20
        }}
      >
        <div style={{ position: "relative", width: 750 }}>
          {/* Every layer below shares the same explicit width so line-wrapping is
              identical across all of them — offsetting via "left/top" (rather than
              "transform") changes each copy's shrink-to-fit available width and made
              copies wrap onto a different number of lines than the visible text. */}
          {KEYWORD_OUTLINE_OFFSETS.map(([dx, dy]) => (
            <div
              key={`kw-${dx}-${dy}`}
              aria-hidden="true"
              style={{
                ...keywordTextStyle,
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                width: 750,
                transform: `translate(${dx}px, ${dy}px)`,
                color: "#000000",
                zIndex: 0
              }}
            >
              {displayKeyword}
            </div>
          ))}
          <div
            style={{
              ...keywordTextStyle,
              position: "relative",
              width: 750,
              color: "#ffffff",
              zIndex: 1
            }}
          >
            {displayKeyword}
          </div>
        </div>
      </div>
    </div>
  );
}
